import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';

const state = {
  mineCount: 0,
  unAssignedCount: 0,
  allCount: 0,
  unattendedCount: 0,
  // AIVIO: account-wide unattended count for the sidebar "Неотвеченные" item.
  // Kept SEPARATE from unattendedCount, which is scoped to the current list
  // view (per-inbox). The sidebar item is account-wide, so it needs its own
  // value that scoped list fetches do not clobber.
  accountUnattendedCount: 0,
};

export const getters = {
  getStats: $state => $state,
  // AIVIO
  getAccountUnattendedCount: $state => $state.accountUnattendedCount,
};

// Create a debounced version of the actual API call function
const fetchMetaData = async (commit, params) => {
  try {
    const response = await ConversationApi.meta(params);
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_TAB_META, meta);
  } catch (error) {
    if (error?.response?.status === 401) {
      window.location.href = '/app/login';
    }
    // ignore other errors
  }
};

const debouncedFetchMetaData = debounce(fetchMetaData, 500, false, 1500);
const longDebouncedFetchMetaData = debounce(fetchMetaData, 5000, false, 10000);
const superLongDebouncedFetchMetaData = debounce(
  fetchMetaData,
  10000,
  false,
  20000
);

// AIVIO: account-wide unattended count (no inbox scope) for the sidebar badge.
// Hits the dedicated meta endpoint (returns unattended_count via the patched
// meta.json.jbuilder) and stores it in a separate field so scoped list-meta
// commits never overwrite it. Debounced to coalesce websocket-driven bursts.
const fetchAccountUnattended = async commit => {
  try {
    // status: 'open' — match the native sidebar "Неотвеченные" view (open
    // unattended only). 'all' would inflate by counting resolved/pending too.
    const {
      data: { meta },
    } = await ConversationApi.meta({ status: 'open', assigneeType: 'all' });
    commit('SET_ACCOUNT_UNATTENDED_COUNT', meta?.unattended_count ?? 0);
  } catch (error) {
    if (error?.response?.status === 401) {
      window.location.href = '/app/login';
    }
    // ignore other errors
  }
};
const debouncedFetchAccountUnattended = debounce(
  fetchAccountUnattended,
  500,
  false,
  5000
);

export const actions = {
  get: async ({ commit, state: $state }, params) => {
    if ($state.allCount > 5000) {
      superLongDebouncedFetchMetaData(commit, params);
    } else if ($state.allCount > 100) {
      longDebouncedFetchMetaData(commit, params);
    } else {
      debouncedFetchMetaData(commit, params);
    }
  },
  set({ commit }, meta) {
    commit(types.SET_CONV_TAB_META, meta);
  },
  // AIVIO: refresh the account-wide unattended count (sidebar badge).
  getAccountWide({ commit }) {
    debouncedFetchAccountUnattended(commit);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
      unattended_count: unattendedCount,
    } = {}
  ) {
    $state.mineCount = mineCount;
    $state.allCount = allCount;
    $state.unAssignedCount = unAssignedCount;
    // AIVIO: only the patched conversations meta endpoint sends unattended_count.
    // List/filter/search meta payloads omit it — keep last-known value instead of
    // clobbering to undefined (which made the badge flicker/disappear on navigation).
    if (unattendedCount !== undefined) {
      $state.unattendedCount = unattendedCount;
    }
    $state.updatedOn = new Date();
  },
  // AIVIO: account-wide unattended count (sidebar badge). Literal string key
  // to avoid touching the shared mutation-types file.
  SET_ACCOUNT_UNATTENDED_COUNT($state, count) {
    $state.accountUnattendedCount = count || 0;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
